/**
 * AddAccountModal — chooser invoked from the Agreement Accounts tab to add a
 * bank Account to the Agreement. Deliberately mirrors AddInsurancePolicyModal
 * (§3h "match-existing") so the two "+ Add X" flows behave identically.
 *
 * Two paths:
 *   1. **Create new account** — navigates to /accounts/new with the Agreement
 *      prefilled (Pattern B, navigate-away; unsaved Agreement edits preserved
 *      via the dirty-form guard).
 *   2. **Attach existing orphan account** — Select over orphan Accounts (both
 *      agreement_id AND opened_by_policy_id NULL → Inactive by the ownership
 *      CHECK). Pattern A: hands the picked id back to the page, which stages
 *      the attach on the Edit transaction and commits on Save (gap-1 / C-167).
 *      The backend attach re-validates (orphan + Inactive, target Active).
 *
 * Presentation-only; the attach mutation lives in the page's Save batch.
 */
import {
  Alert, Badge, Button, Card, Group, Modal, Select, Stack, Text, Title,
} from '@mantine/core';
import { IconAlertCircle, IconBuildingBank, IconLink, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { UUID } from '../api/types';
import { useAccounts } from '../hooks/useAccounts';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The Agreement we are adding an Account to (Active/Prospect — caller gates
   *  the trigger button). */
  agreementId: UUID;
  /** Pattern A staging: the picked orphan Account id is handed back; the page
   *  stages the attach and commits on Save. */
  onStageAttach: (account_id: UUID) => void;
}

type Path = 'choose' | 'create' | 'attach';

export function AddAccountModal({ opened, onClose, agreementId, onStageAttach }: Props) {
  const [path, setPath] = useState<Path>('choose');
  const [selectedId, setSelectedId] = useState<string>('');
  const navigate = useNavigate();
  const returnTo = encodeURIComponent(`/agreements/${agreementId}?tab=accounts`);

  // Orphan Accounts (both FKs NULL → Inactive/Closed), for the attach path.
  // Fetched only while the attach path is open (mirrors the Insurance modal).
  const orphansQ = useAccounts(
    { orphans_only: true, limit: 200 },
    { enabled: opened && path === 'attach' },
  );

  function reset() {
    setPath('choose');
    setSelectedId('');
  }
  function handleClose() {
    reset();
    onClose();
  }
  function handleCreateNew() {
    handleClose();
    navigate(`/accounts/new?agreement_id=${agreementId}&return_to=${returnTo}`);
  }
  function handleAttach() {
    if (!selectedId) return;
    onStageAttach(selectedId as UUID);
    handleClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      title={
        <Group gap="xs">
          <IconBuildingBank size={20} />
          <Title order={4}>Add account</Title>
        </Group>
      }
    >
      {path === 'choose' && (
        <Stack>
          <Text size="sm" c="dimmed">
            Choose how you would like to add a bank Account to this Agreement.
          </Text>

          <Card withBorder padding="md" radius="sm" onClick={() => setPath('create')}
            style={{ cursor: 'pointer' }}>
            <Stack gap={4}>
              <Group gap="xs"><IconPlus size={16} /><Text fw={600}>Create new account</Text></Group>
              <Text size="sm" c="dimmed">
                Start a fresh Account bound to this Agreement. You will fill in
                bank, internal number, IBAN, etc.
              </Text>
            </Stack>
          </Card>

          <Card withBorder padding="md" radius="sm" onClick={() => setPath('attach')}
            style={{ cursor: 'pointer' }}>
            <Stack gap={4}>
              <Group gap="xs"><IconLink size={16} /><Text fw={600}>Attach existing orphan account</Text></Group>
              <Text size="sm" c="dimmed">
                Re-bind an Account that is currently an orphan (Inactive, not
                linked to any Agreement or Policy). It becomes Active and Direct
                on this Agreement when you click Save Changes.
              </Text>
            </Stack>
          </Card>

          <Group justify="flex-end" pt="sm">
            <Button variant="default" onClick={handleClose}>Cancel</Button>
          </Group>
        </Stack>
      )}

      {path === 'create' && (
        <Stack>
          <Alert color="blue" icon={<IconAlertCircle size={16} />}>
            You will be taken to the new-Account form with this Agreement
            prefilled. Unsaved changes on the current Agreement form are
            preserved if you cancel and come back.
          </Alert>
          <Group justify="space-between">
            <Button variant="default" onClick={() => setPath('choose')}>Back</Button>
            <Button leftSection={<IconPlus size={14} />} onClick={handleCreateNew}>
              Open new-account form
            </Button>
          </Group>
        </Stack>
      )}

      {path === 'attach' && (
        <Stack>
          {orphansQ.isLoading && <Text c="dimmed" size="sm">Loading orphan accounts…</Text>}

          {!orphansQ.isLoading && (orphansQ.data?.items.length ?? 0) === 0 && (
            <Alert color="gray" icon={<IconAlertCircle size={16} />}>
              No orphan accounts available. An Account becomes an orphan when it
              is detached from its Agreement (or Policy). Use <em>Create new
              account</em> instead.
            </Alert>
          )}

          {(orphansQ.data?.items.length ?? 0) > 0 && (
            <>
              <Text size="sm" c="dimmed">
                Orphan accounts available to attach to this Agreement:
              </Text>
              <Select
                placeholder="Select an account…"
                data={(orphansQ.data?.items ?? []).map((a) => ({
                  value: a.id,
                  label: `${a.bank_name} — ${a.internal_num}`,
                }))}
                value={selectedId}
                onChange={(v) => setSelectedId(v ?? '')}
                searchable
                allowDeselect={false}
              />
              {selectedId && (
                <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                  Attach moves the Account to <Badge color="green"
                  variant="light">Active</Badge>, Direct on this Agreement, when
                  you click Save Changes.
                </Alert>
              )}
            </>
          )}

          <Group justify="space-between" pt="sm">
            <Button variant="default" onClick={() => setPath('choose')}>Back</Button>
            <Button leftSection={<IconLink size={14} />}
              onClick={handleAttach}
              disabled={!selectedId}>
              Stage attach
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
